/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    memo,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'

// @Material-ui
import {
    FormControl,
    FormControlLabel,
    MenuItem,
    useMediaQuery,
} from '@material-ui/core'
import Grid from '@mui/material/Grid'

// @Components
import RegisterInput, {
    DateMaskCustom,
    ResponsableCPF,
    ResponsableCPFAndCNPJ,
} from 'components/RegisterInput'
import {
    CustomEndAdornment,
    SmToolTip,
} from '../../../../components/RegisterInput/styles'
import CustomSelect from '../../../../components/CustomSelect'

// @Helpers
import {
    GetPreConsumerByToken,
    ValidateDocument,
} from '../../../../redux/api/user'
import { initialFormValues } from '../../helpers/initialValues'
import {
    errorTreatment,
    MASKSCNPJ,
    onBlurStateRegistration,
} from './helpers/errorsTreatment'
import * as Style from './styles'

// @Utils
import {
    EMAIL_DOES_NOT_VALIDATED,
    fillPreForm,
    replaceCpfCnpj,
} from '../../helpers/utils'
import GeneralContext from '../../../../Context'
import { CustomCheckbox } from '../../../../components/CustomCheckbox'
import { GetThemeColorFromStorage } from '../../../../redux/api/themeColor'
import { handleErrorAlert } from '../../functions'
import { Box, Typography } from '@mui/material'

function PersonalData({
    errors,
    control,
    reset,
    watch,
    userCpf,
    setUserCpf,
    setModalAlert,
    setUserData,
    setPreDisabled,
    preDisabled,
    generalRules,
    setIsCnpj,
    cpfParams,
    tenant,
    setUfs,
    clearErrors,
    setValue,
    setError,
    disableItems,
    token,
    userData,
    setDisableItems,
    themeColor,
    setEmancipationStatusInvalid,
}) {
    const { t } = useTranslation()
    const values = watch()

    const { listState } = useContext(GeneralContext)

    const mdMediaQuery = useMediaQuery('(min-width:960px)')

    const handleCpfRemove = () => {
        setPreDisabled(false)
        setUserCpf({})
        setUfs('')
        setValue('dateOfBirth', '')
        reset(initialFormValues)
        setValue('IsError', false)
        setValue('InvalidEmail', false)
        setDisableItems({
            email: false,
            phone: false,
        })
    }

    const replaceStateRegistration = (value) => {
        if (_.isEmpty(value)) return ''

        const stateRegistration = value?.replace(/[^\d]+/g, '')

        return stateRegistration
    }

    function hasMultipleLetters(inscricaoEstadual) {
        if (_.isEmpty(inscricaoEstadual)) return false
        const regex = /[A-Za-z].*[A-Za-z]/
        return regex.test(inscricaoEstadual)
    }

    const clearErrorsCpfCnpj = (value) => {
        if (value.length < 11) {
            setUserCpf({})
        }
        if (value.length > 11 && value.length < 14) {
            setUserCpf({})
        }
    }

    const validateCpf = useCallback(
        async (value) => {
            if (_.isEmpty(value)) return
            const clearValue = replaceCpfCnpj(value)

            if (clearValue.length === 11 || clearValue.length === 14) {
                const { data, success, errors } = await ValidateDocument(
                    value,
                    true,
                    Boolean(tenant === 'pocketexpress'),
                )
                success && setUserCpf({})

                if (errors?.length) {
                    for (const error of errors) {
                        switch (error.message) {
                            case 'EmancipatedStatus - AWAITING_EMANCIPATED':
                                setEmancipationStatusInvalid({
                                    open: true,
                                    text: 'Seu cadastro está em análise. Em breve, você receberá um e-mail com os próximos passos. Fique de olho na sua caixa de entrada!',
                                })
                                return

                            case 'EmancipatedStatus - REJECTED_EMANCIPATED':
                                setEmancipationStatusInvalid({
                                    open: true,
                                    text: 'Seu cadastro foi rejeitado. A imagem do documento não está legível. Por favor, envie uma nova imagem do seu documento.',
                                })
                                break

                            case EMAIL_DOES_NOT_VALIDATED:
                                setModalAlert((prevState) => ({
                                    ...prevState,
                                    modalResendEmailProps: {
                                        open: true,
                                        email: data?.email,
                                    },
                                }))
                                return

                            default:
                                console.warn('Erro não tratado:', error.message)
                                break
                        }
                    }

                    if (tenant === 'pocketexpress') {
                        errorTreatment({
                            errors,
                            setState: setModalAlert,
                            t,
                            value,
                            tenant,
                        })

                        if (errors[0].message === '2FA - NOT_FOUND') {
                            setUserCpf({
                                errors: t(
                                    errors[0].message || '2FA - NOT_FOUND',
                                ),
                            })
                        }
                        handleErrorAlert(errors, data, t, setModalAlert)
                    }

                    if (tenant !== 'pocketexpress') {
                        errorTreatment({
                            errors,
                            setState: setModalAlert,
                            t,
                            value,
                        })

                        const isCNPJ = replaceCpfCnpj(value)?.length > 11

                        const errorMessage =
                            errors[0]?.message === 'DateOfBirth - NOT_FOUND' &&
                            isCNPJ
                                ? t('Foundation_NOT_FOUND')
                                : t(errors[0].message)

                        if (
                            errors[0].message !== '2FA - NOT_FOUND' &&
                            errors[0].message !==
                                'EmancipatedStatus - REJECTED_EMANCIPATED' &&
                            errors[0]?.message !== 'ZipCode - NOT_FOUND' &&
                            errors[0]?.message !== 'DateOfBirth - NOT_FOUND'
                        ) {
                            setUserCpf({ errors: errorMessage })
                        }

                        handleErrorAlert(errors, data, t, setModalAlert)
                    }
                }

                if (data) {
                    setPreDisabled(true)
                    setUserData({
                        ...data,
                        listState: Boolean(data?.stateRegistrationWaiver)
                            ? ''
                            : data?.address?.state?.id,
                    })

                    reset({
                        ...data,
                        cpf:
                            data?.cpf?.length > 11
                                ? MASKSCNPJ(data?.cpf)
                                : data?.cpf,
                        gender: data?.gender,
                        dateOfBirth: data.dateOfBirthMasked,
                        address: {
                            ...data.address,
                            stateId: data?.address?.state?.id || '',
                            cityId: data?.address.city?.id || '',
                            countryId: data?.address?.country?.id || '',
                        },
                        listState: Boolean(data?.stateRegistrationWaiver)
                            ? ''
                            : data?.address?.state?.id,
                        nameRP: data?.representative?.name,
                        cpfRP: data?.representative?.cpf,
                        phoneNumberRP: data?.representative?.phoneNumber,
                        stateRegistration: replaceStateRegistration(
                            data?.stateRegistration,
                        ),
                        emailRP: data?.representative?.email,
                        emailRpPre: data?.representative?.email,
                        stateRegistrationWaiver: Boolean(
                            data?.stateRegistrationWaiver,
                        ),
                        isMEI: Boolean(data?.isMEI),
                        isPreRegistration: 'true',
                        disabledDateOfBirth: !_.isEmpty(
                            data?.dateOfBirthMasked,
                        ),
                        isStateDisabled: hasMultipleLetters(
                            data?.stateRegistration,
                        ),
                        phoneNumber2Pre: _.isEmpty(data?.phoneNumber2)
                            ? null
                            : data?.phoneNumber2,
                        emailPre: _.isEmpty(data?.email)
                            ? undefined
                            : data?.email,
                    })

                    disableContactFields(data)

                    if (tenant !== 'pocketexpress') {
                        for (const error of errors) {
                            const { field, message } = error
                            if (field === 'Email') {
                                setValue('InvalidEmail', true)
                                setDisableItems({
                                    ...disableItems,
                                    email: true,
                                })
                                setError(`email`, {
                                    type: 'manual',
                                    message: t(message),
                                })
                                setUserCpf({})
                            }

                            if (field === 'PhoneNumber2') {
                                setValue('IsError', true)
                                setError(`phoneNumber2`, {
                                    type: 'manual',
                                    message: t(message),
                                })
                                setDisableItems({
                                    ...disableItems,
                                    phone: true,
                                })
                                setUserCpf({})
                            }
                        }
                    }
                }
            }
        },
        [
            reset,
            setError,
            setModalAlert,
            setPreDisabled,
            setUserCpf,
            setUserData,
            t,
            tenant,
        ],
    )

    const getUserByToken = useCallback(async () => {
        if (!token) return

        try {
            const res = await GetPreConsumerByToken(token)

            if (res?.data) {
                const { data } = res?.data

                if (data) {
                    setPreDisabled(true)
                    setUserData(data)
                    fillPreForm(setValue, data)

                    disableContactFields(data)
                }
            }
        } catch (error) {
            console.log(error)
        }
    }, [token])

    useEffect(() => {
        if (token) {
            getUserByToken()
        }
    }, [token])

    const [timerStarted, setTimerStarted] = useState(false)

    const handleKeyUp = () => {
        if (!timerStarted) {
            setTimerStarted(true)
        }
    }

    const disableContactFields = (data) => {
        if (_.isEmpty(data?.phoneNumber2)) {
            setDisableItems({
                ...disableItems,
                phone: true,
            })
        }

        if (_.isEmpty(data?.email)) {
            setDisableItems({
                ...disableItems,
                email: true,
            })
        }
    }

    useEffect(() => {
        let typingTimer

        if (watch('listState')) {
            if (!timerStarted) {
                setTimerStarted(true)
            }

            clearTimeout(typingTimer)
            typingTimer = setTimeout(() => {
                onBlurStateRegistration(
                    watch,
                    setError,
                    clearErrors,
                    t,
                    setTimerStarted,
                )
            }, 3000)
        }

        return () => clearTimeout(typingTimer)
    }, [watch('stateRegistration'), watch('listState')])

    useEffect(() => {
        const cpfCnpj = replaceCpfCnpj(values?.cpf)
        if (!cpfCnpj || token) return

        setIsCnpj(cpfCnpj?.length > 11)
        validateCpf(values?.cpf)
    }, [values?.cpf])

    return (
        <Style.Container
            container
            item
            spacing={2}
            xs={12}
            mdMediaQuery={mdMediaQuery}
            disabled={preDisabled}
        >
            <Grid item xs={12} textAlign={'center'}>
                <Typography
                    variant="body1"
                    component="strong"
                    fontSize="18px"
                    style={{ color: '#1f1f1f', fontWeight: 600 }}
                >
                    CADASTRO
                </Typography>
            </Grid>

            <Style.CustomTitle>
                <Box
                    component="strong"
                    textTransform="uppercase"
                    style={{ color: '#1f1f1f', fontWeight: 600 }}
                >
                    {replaceCpfCnpj(values.cpf)?.length > 11
                        ? t('FIELD_COMPANY_DATA')
                        : t('FIELD_PERSONAL_DATA')}
                </Box>
            </Style.CustomTitle>
            <Controller
                render={({ value, onChange, ...renderProps }) => (
                    <RegisterInput
                        {...renderProps}
                        value={value}
                        onChange={(e) => {
                            clearErrorsCpfCnpj(
                                replaceCpfCnpj(e?.target?.value || 0),
                            )
                            onChange(e)
                        }}
                        style={{ alignItems: 'flex-start' }}
                        xs={12}
                        md={4}
                        disabled={userCpf?.blockCpf || preDisabled}
                        label={
                            generalRules?.corporationPartners
                                ? 'CPF/CNPJ'
                                : 'CPF'
                        }
                        InputProps={{
                            inputComponent: generalRules?.corporationPartners
                                ? ResponsableCPFAndCNPJ
                                : ResponsableCPF,
                            endAdornment: (
                                <CustomEndAdornment
                                    style={{ paddingLeft: '10px' }}
                                >
                                    {!!values.cpf?.length && (
                                        <div onClick={handleCpfRemove}>
                                            <i
                                                className="icon-ico_excluir"
                                                style={{
                                                    color: themeColor
                                                        ?.navigation?.buttons
                                                        ?.backgroundColor,
                                                }}
                                            />
                                        </div>
                                    )}
                                </CustomEndAdornment>
                            ),
                        }}
                        error={userCpf?.errors || !!errors?.cpf}
                        hasTooltip
                        helperText={t(userCpf?.errors || errors?.cpf?.message)}
                    />
                )}
                name="cpf"
                control={control}
                id="cpf"
            />

            <Controller
                as={
                    <RegisterInput
                        xs={12}
                        md={8}
                        label={
                            replaceCpfCnpj(values.cpf)?.length <= 11
                                ? t('FIELD_REGISTER_NAME')
                                : t('CORPORATE_NAME')
                        }
                        disabled={
                            userCpf.blockCpf ||
                            (preDisabled &&
                                !token &&
                                tenant !== 'pocketexpress' &&
                                userData?.name)
                        }
                        error={!!errors.name}
                        helperText={t(errors?.name?.message)}
                        style={{ alignItems: 'flex-start' }}
                    />
                }
                name="name"
                control={control}
                id="name"
            />

            <Grid item xs={12} sm={12} md={4} lg={4} container>
                <Controller
                    as={
                        <RegisterInput
                            style={{ alignItems: 'flex-start' }}
                            label={
                                replaceCpfCnpj(values.cpf)?.length <= 11
                                    ? t('TITLE_REGISTER_BIRTHDAY')
                                    : t('DT_BASE')
                            }
                            xs={12}
                            md={12}
                            disabled={
                                userCpf.blockCpf
                                // || watch('disabledDateOfBirth')
                            }
                            error={!!errors.dateOfBirth}
                            helperText={t(errors?.dateOfBirth?.message)}
                            InputProps={{
                                inputComponent: DateMaskCustom,
                            }}
                        />
                    }
                    name="dateOfBirth"
                    control={control}
                    id="dateOfBirth"
                />
            </Grid>
            {replaceCpfCnpj(values.cpf)?.length > 11 && (
                <Grid
                    item
                    xs={12}
                    md={7}
                    lg={7}
                    container
                    className="containerWaiverCheckbox small-input-checkbox"
                    id="isStateDisabled-div"
                    style={{ alignItems: 'flex-start' }}
                >
                    <FormControl>
                        <Controller
                            render={({ value, onChange }) => {
                                return (
                                    <FormControlLabel
                                        control={
                                            <CustomCheckbox
                                                checked={Boolean(values?.isMEI)}
                                                disabled={
                                                    preDisabled &&
                                                    !token &&
                                                    userData?.isMEI
                                                }
                                                onChange={(e) => {
                                                    onChange(!value)
                                                }}
                                                name="checkedB"
                                            />
                                        }
                                        label={t('YOUR_COMPANY_FITS_MEI')}
                                    />
                                )
                            }}
                            name="isMEI"
                            id="isMEI"
                            control={control}
                        />
                    </FormControl>
                </Grid>
            )}
            {replaceCpfCnpj(values.cpf)?.length > 11 && (
                <>
                    <Grid
                        item
                        xs={12}
                        md={4}
                        justify="flex-start"
                        alignItems="center"
                        className="containerWaiverCheckbox small-input-checkbox"
                        id="isStateDisabled-div"
                    >
                        <Controller
                            render={({ value, onChange }) => {
                                return (
                                    <FormControlLabel
                                        control={
                                            <CustomCheckbox
                                                checked={Boolean(
                                                    values?.stateRegistrationWaiver,
                                                )}
                                                disabled={
                                                    preDisabled &&
                                                    !token &&
                                                    !watch('isStateDisabled') &&
                                                    userData?.stateRegistrationWaiver
                                                }
                                                onChange={(e) => {
                                                    onChange(!value)
                                                    setValue(
                                                        'stateRegistration',
                                                        '',
                                                    )
                                                    setValue('listState', '')
                                                    clearErrors(
                                                        'stateRegistration',
                                                    )
                                                    clearErrors('listState')
                                                }}
                                                name="checkedB"
                                                color="primary"
                                            />
                                        }
                                        label={t('IE_EXEMPT')}
                                    />
                                )
                            }}
                            name="stateRegistrationWaiver"
                            id="stateRegistrationWaiver"
                            control={control}
                        />
                    </Grid>
                    <Controller
                        render={(props) => (
                            <RegisterInput
                                style={{ alignItems: 'flex-start' }}
                                label={t('UF Inscrição Estadual')}
                                select
                                xs={12}
                                md={4}
                                disabled={
                                    values?.stateRegistrationWaiver ||
                                    (preDisabled &&
                                        !token &&
                                        !watch('isStateDisabled') &&
                                        userData?.listState &&
                                        !errors?.listState)
                                }
                                error={!!errors.listState}
                                helperText={t(errors?.listState?.message)}
                                list={listState}
                                {...props}
                            />
                        )}
                        name="listState"
                        control={control}
                    />
                    <Controller
                        render={({ value, onChange }) => (
                            <RegisterInput
                                style={{ alignItems: 'flex-start' }}
                                xs={12}
                                md={4}
                                label={t('Sua Inscrição Estadual')}
                                disabled={
                                    values?.stateRegistrationWaiver ||
                                    (preDisabled &&
                                        !token &&
                                        !watch('isStateDisabled') &&
                                        userData?.stateRegistration &&
                                        !errors?.stateRegistration)
                                }
                                onKeyUp={handleKeyUp}
                                onChange={(e) => {
                                    if (e?.target?.value?.length > 30) return
                                    onChange(e.target.value)
                                }}
                                value={value}
                                name="stateRegistration"
                                hasTooltip
                                error={!!errors.stateRegistration}
                                helperText={t(
                                    errors?.stateRegistration?.message,
                                )}
                            />
                        )}
                        name="stateRegistration"
                        control={control}
                        id="stateRegistration"
                    />
                </>
            )}
            {replaceCpfCnpj(values.cpf)?.length <= 11 &&
                !generalRules?.allowSimplifyConsumerRegistration && (
                    <>
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={4}
                            style={{
                                marginTop: '8px',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Controller
                                as={
                                    <CustomSelect
                                        id="label"
                                        label="Gênero"
                                        error={errors.gender}
                                        errorMsg={
                                            errors.gender &&
                                            t(errors.gender.message)
                                        }
                                    >
                                        <MenuItem value="M">Masculino</MenuItem>
                                        <MenuItem value="F">Feminino</MenuItem>
                                        <MenuItem value="NDA">
                                            Não informar
                                        </MenuItem>
                                    </CustomSelect>
                                }
                                name="gender"
                                id="gender"
                                control={control}
                            />
                        </Grid>
                    </>
                )}
        </Style.Container>
    )
}

export default memo(PersonalData)
